import type { MapObject, GeoArea } from '../../lib/types'

/** A picked stand on a drive. `id` is a stable local key (the Drives sub-tree
 *  keys/operates by stand id, never array index). `pinId` references a real map
 *  pin, or is `null` for an "unplaced" stand rebuilt from a draft's `standCount`
 *  on reload (Phase C load path). `assignedId` is the assigned member id. */
export interface PlanHuntStand {
  id: string
  pinId: string | null
  assignedId: string | null
}

/** Sentinel for `PlanHuntDrive.area`: the drive's base is the entire hunting
 *  ground rather than one sub-area. `null` still means "not chosen yet" — the
 *  hunter must pick a base before stands become selectable. */
export const ENTIRE_GROUND = 'entire'

/** A drive in wizard-local state. `id` is a stable local key (DriveCard/DrivesStep
 *  reorder and key by it). No start/end times. `subAreaIds`/`pinIds` are the
 *  drive's source of truth and the wire-contract lists it round-trips through
 *  the API: the multi-select editor writes both directly (an empty
 *  `subAreaIds` means the whole hunting ground), and the converter sends them
 *  straight through on save rather than deriving them from anything else. The
 *  singular `area` field (`null` = not chosen yet, ENTIRE_GROUND = whole
 *  hunting ground, anything else = a sub-area id) is vestigial: nothing writes
 *  it from the multi-select editor any more, and it is read only for
 *  legacy-compat by the converter's load path (lifting an old draft's single
 *  `area` into a one-element `subAreaIds` when the draft predates the
 *  multi-select). Optional so existing call sites that only set `area` keep
 *  compiling; absent `subAreaIds`/`pinIds` is equivalent to `[]`. */
export interface PlanHuntDrive {
  id: string
  name: string
  area: string | null
  subAreaIds?: string[]
  pinIds?: string[]
  stands: PlanHuntStand[]
}

/** A team member usable as leader and/or participant. */
export interface PlanHuntMember {
  id: string
  name: string
  role?: string
  avatar?: string | null
  /** True for the signed-in user (prototype: `p-erik`). */
  you?: boolean
}

/** A team option for the Basics step. `area`/`members` are optional extras the
 *  prototype shows; the domain passes only `id` + `name`. */
export interface PlanHuntTeamOption {
  id: string
  name: string
  area?: string
  members?: number
  /** True for an estate team — shows the estate tag beside the name in the picker. */
  isEstate?: boolean
}

/** One chosen class/category of a species and its own quota. `limit` null/absent
 *  = "No limit" (the default when a class is picked). Mirrors the mobile
 *  GameSubtype (name + amount) so per-category quotas round-trip between apps. */
export interface PlanHuntGameClass {
  name: string
  limit?: number | null
}

/** A game selection: which species + which classes are checked, each class with
 *  its own quota (e.g. moose 1 calf / 2 cow / 6 bull). Structurally identical to
 *  the domain's `HuntableGameSelectionDetail`, so the two are mutually
 *  assignable. The catalog itself reuses `HuntableGameSpecies` from `@wehunt/ui`
 *  (`lib/huntable-game-catalog`) — do NOT redefine it. */
export interface PlanHuntGameSelection {
  species: string
  classes: PlanHuntGameClass[]
}

/** An attachable document (team-owned or device-uploaded). */
export interface PlanHuntDocItem {
  id: string
  name: string
  kind: 'pdf' | 'doc' | 'xls' | 'img'
  size?: string
  src?: string
}

export type { MapObject, GeoArea }
